import type { getSupabaseClient } from "./supabase/client";

type SupabaseLike = ReturnType<typeof getSupabaseClient>;

/**
 * Remove any leftover *unverified* TOTP factors (e.g. from an abandoned
 * enrolment) so a fresh enroll doesn't collide with the hardcoded
 * "Authenticator App" friendly name. Verified/active factors are never
 * touched. Mirrors the mobile cleanup behaviour.
 */
export async function cleanupUnverifiedTotpFactors(supabase: SupabaseLike): Promise<void> {
  if (!supabase) return;
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error || !data) return;
  const stale = (data.totp || []).filter((f) => f.status !== "verified");
  for (const f of stale) {
    await supabase.auth.mfa.unenroll({ factorId: f.id }).catch(() => undefined);
  }
}

/**
 * Enroll a new TOTP factor, self-healing around abandoned unverified
 * factors. Server cleanup runs first (it sees unverified factors the
 * browser client can't), then the client-side cleanup as a secondary
 * measure. If Supabase still reports a name collision ("already exists"),
 * we clean up once more and retry the enroll a single time.
 */
export async function enrollTotpWithCleanup(params: {
  supabase: NonNullable<SupabaseLike>;
  friendlyName: string;
  serverCleanup: () => Promise<void>;
}) {
  const { supabase, friendlyName, serverCleanup } = params;
  await serverCleanup();
  await cleanupUnverifiedTotpFactors(supabase);
  let result = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName });
  if (result.error && /already exists/i.test(result.error.message)) {
    await serverCleanup();
    await cleanupUnverifiedTotpFactors(supabase);
    result = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName });
  }
  return result;
}

/**
 * Cancel/abandon an in-progress enrolment: remove the freshly created
 * unverified factor so it doesn't linger and block the next attempt.
 * Never disturbs an already-verified factor.
 */
export async function cancelTotpEnrollment(params: {
  supabase: SupabaseLike;
  mfaEnabled: boolean;
  mfaFactorId: string | null;
}): Promise<void> {
  const { supabase, mfaEnabled, mfaFactorId } = params;
  if (supabase && !mfaEnabled && mfaFactorId) {
    await supabase.auth.mfa.unenroll({ factorId: mfaFactorId }).catch(() => undefined);
  }
}
